<template>
  <v-dialog
    hide-overlay
    :overlay-opacity="0"
    :transition="false"
    persistent
    ref="dialog"
    width="561px"
    :value="isOpen ? true : false"
    absolute
  >
    <div class="card-overlay" @click="$refs.dialog.animateClick()"/>
    <v-card style="width: 561px" v-if="isOpen">
      <template v-if="Object.keys(selectedTheme).length && selectedTheme.screenshot_url">
        <v-img
          height="285px"
          width="100%"
          position="top center"
          :src="selectedTheme.screenshot_url"
        />
        <v-divider/>
      </template>
      <v-card-text height="800px" class="px-10 pt-8">
        <v-btn x-small icon @click="$emit('removeModal')" class="close-model">
          <v-icon>$close</v-icon>
        </v-btn>

        <v-form ref="form">
          <h6>Choose User</h6>
          <p class="p-3 mt-2">The chosen users will receive full access to a new instance and hosting</p>
          <v-select
            outlined
            :items="ownerOptions"
            v-model="owner"
            hide-details=""
            validate-on-blur
            class="v-input--lg"
            item-value="id"
            :menu-props="{
              offsetY: true,
              nudgeBottom: '8px',
              closeOnContentClick: false,
              contentClass:
                'custom-dropdown-select custom-dropdown-select--onboarding',
            }"
          >
            <template v-slot:selection="{ item }">
              <span>{{ $t(`form.option.${item}`) }}</span>
            </template>
            <template v-slot:item="{ item }">
              <span>{{ $t(`form.option.${item}`) }}</span>
            </template>
          </v-select>

          <v-combobox
            v-if="owner === 'me_and_delegated_user'"
            :placeholder="$t('form.placeholder.emailDelegatedUser')"
            outlined
            :items="userSuggestions"
            :loading="userSuggestionsLoading"
            :search-input.sync="userSuggestionsSearch"
            v-model="selectedUser"
            @change="selectedUser = normalizeSelectedUser($event)"
            single-line
            :multiple="false"
            :filter="
              (item, queryText) => {
                return (
                  (item.email + item.name)
                    .toLocaleLowerCase()
                    .indexOf(queryText.toLocaleLowerCase()) > -1
                );
              }
            "
            hide-details=""
            item-value="email"
            item-text="name"
            :menu-props="{
              offsetY: true,
              nudgeBottom: '8px',
              closeOnContentClick: true,
              contentClass:
                'custom-dropdown-select custom-dropdown-select--autocomplete custom-dropdown-select--autocomplete-share',
            }"
          >
            <template v-slot:selection="{ item }">
              <v-chip
                small
                :class="isEmailCorrect(item) ? '' : 'v-chip--error'"
              >
                <span class="text-truncate">
                  {{ typeof item === "object" ? item.name : item }}
                </span>
                <v-icon
                  @click.stop="clearSelection"
                  v-ripple
                  size="16"
                  class="ml-1 delete-button"
                >
                  $close
                </v-icon>
              </v-chip>
            </template>
            <template v-slot:item="{ item }">
              <div class="d-flex align-center">
                <template v-if="item.email === null">
                  <p class="s p-2 mb-0 font-weight-bold gray--text text--darken-2">
                    {{ item.name }}
                  </p>
                </template>
                <template v-else>
                  <v-avatar size="32px">
                    <v-img :src="getAvatarUrl(item.email, 32)"/>
                  </v-avatar>
                  <div class="d-flex flex-column ml-3">
                    <p class="s p-2 mb-0 font-weight-bold gray--text text--darken-2">
                      {{ item.name }}
                    </p>
                    <p class="p-3 mb-0 mt-1" v-if="item.name !== item.email">
                      {{ item.email }}
                    </p>
                  </div>
                </template>
              </div>
            </template>
          </v-combobox>
        </v-form>
      </v-card-text>
      <template v-if="isImport">
        <v-card-actions class="pt-0 d-flex align-center justify-space-between">
          <div>
            <step-indicator :currentStep="3" :maxSteps="4"/>
          </div>
          <div class="d-flex align-center">
            <v-btn
              large
              @click="$emit('removeModal')"
              text
              elevation="0"
              class="mr-3"
            >
              <span class="font-weight-bold gray--text text--darken-1">
                {{ $t('button.back') }}
              </span>
            </v-btn>
            <v-btn
              large
              @click="selectUser"
              color="primary"
              elevation="0"
            >
              <span class="font-weight-bold">
                <span>{{ $t('button.continue') }}</span>
                <v-icon size="20" class="mr-0">$arrowright</v-icon>
              </span>
            </v-btn>
          </div>
        </v-card-actions>
      </template>
      <template v-else>
        <v-card-actions class="d-flex flex-column pb-4 px-10 pt-2">
          <v-btn
            x-large
            elevation="0"
            color="primary"
            block
            :disabled="owner === 'me_and_delegated_user' &&  !isEmailCorrect(selectedUser)"
            @click="selectUser"
          >
            <!-- Super Quick Onboarding -->
            <template
              v-if="(selectedService.install_method == 'quick' || selectedService.install_method == 'super_quick') &&
                selectedService.onboarding.user_configurable_options.length > 0
              "
            >
              {{ $t(`button.set.${configurableOptionName}`) }}
              <v-icon size="24" class="ml-2">$arrowright</v-icon>
            </template>
            <template
              v-else-if="
              selectedService.install_method == 'super_quick' &&
              selectedService.ask_for_domain
            "
            >
              {{ $t("button.domain.set") }}
              <v-icon size="24" class="ml-2">$arrowright</v-icon>
            </template>
            <template
              v-else-if="
              selectedService.install_method == 'super_quick' &&
              selectedService.onboarding.super_quick_ask_for_site_name
            "
            >
              {{ $t("button.website.set") }}
              <v-icon size="24" class="ml-2">$arrowright</v-icon>
            </template>
            <template
              v-else-if="selectedService.install_method == 'super_quick'"
            >
              {{ $t("button.website.create") }}
              <v-icon size="24" class="ml-2">$arrowright</v-icon>
            </template>

            <!-- Quick Onboarding -->
            <template
              v-else-if="
              selectedService.install_method == 'quick'
              && selectedService.is_installed_from_template
              && selectedService.quick_allow_to_choose_themes
              && !selectedTheme
            "
            >
              {{ $t("button.theme.choose") }}
              <v-icon size="24" class="ml-2">$arrowright</v-icon>
            </template>
            <template
              v-else-if="
              selectedService.install_method == 'quick'
              && selectedService.is_installed_from_template
              && !selectedService.quick_allow_to_choose_themes
              && selectedService.ask_for_domain
            "
            >
              {{ $t("button.domain.set") }}
              <v-icon size="24" class="ml-2">$arrowright</v-icon>
            </template>
            <template
              v-else-if="
              selectedService.install_method == 'quick'
              && selectedService.is_installed_from_template
              && !selectedService.quick_allow_to_choose_themes
              && !selectedService.ask_for_domain
            "
            >
              {{ $t("button.website.set") }}
              <v-icon size="24" class="ml-2">$arrowright</v-icon>
            </template>

            <!-- Standard Onboarding -->
            <template
              v-else-if="
              !selectedTheme.name &&
              selectedService.choose_theme &&
              !selectedService.plan_default_theme
            "
            >
              {{ $t("button.theme.choose") }}
              <v-icon size="24" class="ml-2">$arrowright</v-icon>
            </template>
            <template v-else-if="selectedService.ask_for_domain">
              {{ $t("button.domain.set") }}
              <v-icon size="24" class="ml-2">$arrowright</v-icon>
            </template>
            <template v-else>
              {{ $t("button.website.set") }}
              <v-icon size="24" class="ml-2">$arrowright</v-icon>
            </template>
          </v-btn>
          <v-btn
            v-if="services.length > 1"
            @click="$emit('change-service-button-clicked')"
            x-large
            elevation="0"
            class="mx-0 mt-2"
            color="gray"
            text
            block
          >
            <span class="font-weight-light gray--text text--darken-1">
              {{ $t("button.service.change") }}
            </span>
          </v-btn>
          <v-btn
            v-else-if="
              !selectedService.plan_default_theme &&
              selectedService.install_method !== 'super_quick'
            "
            @click="$emit('change-theme-button-clicked')"
            x-large
            elevation="0"
            class="mx-0 mt-2"
            color="gray"
            text
            block
          >
            <span class="font-weight-light gray--text text--darken-1">
              {{ $t("button.theme.change") }}
            </span>
          </v-btn>
          <v-btn
            v-else
            @click="$emit('removeModal')"
            x-large
            elevation="0"
            class="mx-0 mt-2"
            color="gray"
            text
            block
          >
            <span class="font-weight-light gray--text text--darken-1">
              {{ $t("button.cancel") }}
            </span>
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import FormRulesMixin from "../../mixins/FormRulesMixin";
import Api from "../../apis/Api";
import {getAvatarUrl} from "../../apis/HelperFunctions";
import StepIndicator from "../StepIndicator.vue";

export default {
  name: "OnboardingChooseUserModal",
  components: {StepIndicator},
  mixins: [FormRulesMixin],
  props: {
    selectedService: Object,
    selectedTheme: Object,
    services: Array,
    isOpen: [Boolean, String],
    isImport: Boolean,
  },
  data() {
    return {
      getAvatarUrl,
      selectedUser: "",
      userSuggestions: [],
      userSuggestionsFetched: false,
      userSuggestionsLoading: false,
      userSuggestionsSearch: "",
      owner: 'only_me',
      ownerOptions: [
        'only_me',
        'me_and_delegated_user'
      ]
    }
  },
  computed: {
    configurableOptionName() {
      if (this.selectedService.onboarding.user_configurable_options.length > 0) {
        return this.selectedService.onboarding.user_configurable_options[0].name;
      }
      return null;
    }
  },
  methods: {
    selectUser() {
      this.$emit('delegated-user-selected', this.selectedUser);
      this.selectedUser = "";
      this.owner = 'only_me';
    },
    clearSelection() {
      this.selectedUser = "";
    },
    isEmailCorrect: function (item) {
      if (typeof item === "object") {
        return this.validateEmail(item.email);
      } else {
        return this.validateEmail(item);
      }
    },
    validateEmail(email) {
      return /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email);
    },
    normalizeSelectedUser(value) {
      if (typeof value === 'object') {
        return value.email;
      }
      return value;
    }
  },
  watch: {
    isOpen: function (open) {
      if (open) {
        this.$store.dispatch("lockBodyScroll");
      }
      this.$store.dispatch("unlockBodyScroll");
    },
    owner: {
      handler(newValue) {
        if (newValue === 'only_me') {
          this.selectedUser = "";
        }
      },
      immediate: true
    },
    userSuggestionsSearch() {
      if (this.userSuggestionsFetched) {
        return;
      }
      this.userSuggestionsFetched = true;
      this.userSuggestionsLoading = true;
      Api.get("/invitation-suggestions")
        .then((response) => {
          this.userSuggestions = response.data.data;
        })
        .catch((error) => {
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.userSuggestionsLoading = false;
        });
    },
  },
}
</script>

<style lang="scss" scoped>
.v-card {
  .v-card__text {
    -webkit-line-clamp: none;
    -webkit-box-orient: vertical;
    color: var(--v-gray-lighten1);
  }

  @media (max-width: 975px) {
    .v-card__text {
      padding: 0 24px !important;
    }

    .v-card__title {
      padding: 24px !important;
    }

    .v-card__actions {
      padding: 24px 24px 12px 24px !important;
    }

    .combobox {
      width: 100%;
      flex-grow: 1;
    }
  }
}

.v-expansion-panel::v-deep {
  &:not(:last-child) {
    margin-bottom: 32px;
  }

  &::before,
  &::after {
    display: none;
  }

  .v-expansion-panel-content {
    &__wrap {
      padding-bottom: 0px;
      padding-top: 32px;

      > div:not(:last-child) {
        margin-bottom: 16px;
      }
    }

    .checkbox {
      label {
        margin-left: 12px;
        color: var(--v-gray-darken2);
        font-size: $font-size-base;
        line-height: $line-height-base;
        font-weight: $font-weight-light;
      }

      &--error-label {
        label {
          font-weight: $font-weight-bold;
          color: var(--v-error-base);
        }
      }

      &--info-label {
        label {
          font-weight: $font-weight-bold;
          color: var(--v-info-base);
        }
      }
    }
  }

  .v-expansion-panel-header {
    padding: 0;
    min-height: unset;
    background: transparent !important;

    .chevron {
      color: var(--v-gray-lighten1);
    }

    &--active {
      .chevron {
        color: var(--v-primary-base) !important;
        transform: rotate(180deg);
        transition: color 0.24s ease, transform 0.24s ease;
      }
    }

    &:hover {
      background: transparent !important;
    }

    & > *:not(.v-expansion-panel-header__icon) {
      flex: 0 1 auto;
    }
  }
}

.v-autocomplete::v-deep {
  .v-chip {
    background: transparent;
    border: 1px solid var(--v-gray-lighten3);
    border-radius: 5px;
    color: var(--v-gray-darken2);
    padding: 4px;
    font-size: $font-size-sm;
    line-height: $line-height-sm;
    font-weight: $font-weight-normal;
    height: 28px;
    // max-width: calc(100% - 32px);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    .v-chip__content {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .delete-button {
      color: var(--v-gray-darken1);
    }

    &.v-chip--error {
      border: 1px solid var(--v-error-base);
      color: var(--v-error-base);

      .delete-button {
        color: var(--v-error-base);
      }
    }
  }

  .v-input__append-inner {
    padding-left: 0px !important;
  }

  .v-input__control .v-input__slot {
    max-height: unset !important;
    height: fit-content;

    .v-select__selections {
      min-height: unset;
      padding: 0;

      input {
        min-width: 126px;
        max-width: unset;
        max-height: unset;
      }
    }
  }

  .v-select__selections {
    padding: 0px 0 !important;
  }

  input {
    padding: 0px !important;
  }

  &.v-select--is-menu-active {
    .v-input__append-inner {
      .v-icon {
        color: map-get($primary, darken1) !important;
        transform-origin: center;
        transform: rotate(180deg);
      }
    }
  }
}

.checkbox::v-deep {
  margin-right: 12px;

  .v-input.v-input--checkbox .v-input__control .v-input__slot {
    min-height: unset;
  }
}

.input-dropdown::v-deep {
  .v-select__selections .v-select__selection {
    font-size: $font-size-base;
    line-height: $line-height-base;
    font-weight: $font-weight-light;
    color: var(--v-text-base);
    margin: 0px;
  }
}

.user-list {
  display: flex;
  flex-direction: column;
  max-height: 260px;
  overflow-y: auto !important;
  margin-left: -8px;

  &::-webkit-scrollbar {
    width: 14px;
  }

  &::-webkit-scrollbar-track {
    box-shadow: none;
    background-color: transparent;
    max-width: 8px;
    border: 3px solid transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--v-gray-lighten2);
    border-radius: 8px;
    border: 4px solid transparent;
    background-clip: padding-box;
  }
}

.v-avatar {
  border-color: var(--v-gray-lighten2) !important;
  border-style: solid !important;
  border-width: 1px !important;
}

.none-option {
  color: #757575;
  font-style: italic;
}
</style>